import { ReactNode, useState } from 'react'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Alert
} from '@mui/material'
import { styled } from '@mui/material/styles'
import ButtonBase from 'design-system/button'
import { useLogo } from 'hooks/useLogo'
import Storage from 'storage/auth'
import { acceptTerms } from 'services/user'
import { ITermsConfirmation } from 'types'
import { TERMS_CONFIRMATIONS } from './constants'

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minHeight: '100vh',
  gap: theme.spacing(2),
  maxWidth: '760px',
  padding: theme.spacing(4, 5),
  margin: '0 auto'
}))

const PreviewFrame = styled('iframe')(({ theme }) => ({
  width: '100%',
  height: '50vh',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  background: theme.palette.background.default
}))

const TERMS_URL = '/terms_and_conditions_pt.html'
const PRIVACY_URL = '/privacy_policy_pt.html'
const ERROR_MESSAGE = 'Não foi possível registrar o aceite. Tente novamente.'

type AcceptanceItem = 'terms' | 'privacy' | ITermsConfirmation

const ACCEPTANCE_ITEMS: ReadonlyArray<{
  id: AcceptanceItem
  label: ReactNode
}> = [
  { id: 'terms', label: 'Li e aceito os Termos de Uso.' },
  { id: 'privacy', label: 'Li e aceito as Políticas de Privacidade.' },
  ...TERMS_CONFIRMATIONS
]

const alreadyAcceptedItems = (): AcceptanceItem[] => {
  const user = Storage.user.get()
  const termsItems: AcceptanceItem[] =
    user?.terms_accepted === true ? ['terms', 'privacy'] : []
  const confirmations = TERMS_CONFIRMATIONS.map(({ id }) => id).filter(id =>
    user?.accepted_confirmations?.includes(id)
  )
  return [...termsItems, ...confirmations]
}

interface AcceptTermsProps {
  /** Called after the terms are successfully accepted */
  onAccepted: () => void
}

/**
 * Blocking screen shown after login while the professional has not accepted the
 * Terms of Use, the Privacy Policy or any of the AI confirmations. Items already
 * accepted in the session come checked and locked.
 */
const AcceptTerms = ({ onAccepted }: AcceptTermsProps) => {
  const logo = useLogo()
  const [tab, setTab] = useState(0)
  const [locked] = useState(alreadyAcceptedItems)
  const [checked, setChecked] = useState<AcceptanceItem[]>(locked)
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>(
    'idle'
  )

  const allChecked = ACCEPTANCE_ITEMS.every(({ id }) => checked.includes(id))

  const toggle = (id: AcceptanceItem, value: boolean) => {
    setChecked(current =>
      value ? [...current, id] : current.filter(item => item !== id)
    )
  }

  const handleAccept = async () => {
    setStatus('submitting')
    try {
      const confirmations = TERMS_CONFIRMATIONS.map(({ id }) => id)
      const response = await acceptTerms(confirmations)
      if (!response.success) {
        setStatus('error')
        return
      }
      const user = Storage.user.get()
      if (user) {
        const accepted = user.accepted_confirmations ?? []
        Storage.user.set({
          ...user,
          terms_accepted: true,
          accepted_confirmations: [
            ...accepted,
            ...confirmations.filter(id => !accepted.includes(id))
          ]
        })
      }
      onAccepted()
    } catch {
      setStatus('error')
    }
  }

  return (
    <Container>
      <img src={logo} alt='Sentinela' style={{ width: '240px' }} />
      <Typography variant='h6' textAlign='center'>
        Termos de Uso e Políticas de Privacidade
      </Typography>
      <Typography variant='body1' color='textSecondary' textAlign='center'>
        Para continuar, leia e aceite os Termos de Uso e as Políticas de
        Privacidade e confirme os itens abaixo.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant='fullWidth'
        sx={{ width: '100%' }}
      >
        <Tab label='Termos de Uso' />
        <Tab label='Políticas de Privacidade' />
      </Tabs>

      <PreviewFrame
        title={tab === 0 ? 'Termos de Uso' : 'Políticas de Privacidade'}
        src={tab === 0 ? TERMS_URL : PRIVACY_URL}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'flex-start'
        }}
      >
        {ACCEPTANCE_ITEMS.map(({ id, label }) => (
          <FormControlLabel
            key={id}
            disabled={locked.includes(id)}
            control={
              <Checkbox
                checked={checked.includes(id)}
                onChange={(_, value) => toggle(id, value)}
              />
            }
            label={label}
          />
        ))}
      </Box>

      {status === 'error' && (
        <Alert severity='error' sx={{ width: '100%' }}>
          {ERROR_MESSAGE}
        </Alert>
      )}

      <ButtonBase
        onClick={handleAccept}
        disabled={!allChecked || status === 'submitting'}
        sx={{ width: '100%' }}
      >
        Aceitar e continuar
      </ButtonBase>
    </Container>
  )
}

export default AcceptTerms
